import { View } from 'react-native';
import { colors, formatLongDate, formatMinutes, formatTime, pick, reasonLabels, statusLabels, summarize, tasksInPlan } from '@hogar/shared';
import { Txt, useT } from '../components/ui';
import { Logo } from '../components/Logo';
import { Box, Paper } from '../components/Paper';
import { toRestock, useHouse } from '../lib/house';
import { crewSlice } from '../lib/crew';
import { useLocalSearchParams } from 'expo-router';
import { crewLabels } from '@hogar/shared';

/** The daily report as a printable page, in the owner's language. */
export default function ReportPrint() {
  const { t, lang } = useT();
  const { crew: crewParam } = useLocalSearchParams<{ crew?: string }>();
  const crew = crewParam === 'garden' ? 'garden' : 'house';
  const snap = useHouse();
  const { tasks, products } = crewSlice(snap, crew);
  const { household, today, todayDate } = snap;
  const todays = tasksInPlan(snap.plans[today.date]?.[crew], today.weekday, tasks);
  const s = summarize(todays, today.results);
  const restock = toRestock(products);

  return (
    <Paper>
      <Logo size={0.7} tagline={false} />
      <Txt weight="semibold" size={16} style={{ textAlign: 'center', marginTop: 10 }}>
        {t('todaysReport')} · {household.name}{crew === 'garden' ? ` · ${crewLabels.garden[lang]}` : ''}
      </Txt>
      <Txt size={13} color={colors.textMuted} style={{ textAlign: 'center', marginBottom: 14 }}>
        {formatLongDate(todayDate, lang)}
      </Txt>
      <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: 14 }}>
        <Stat big={`${s.done}/${s.total}`} small={t('tasksCompleted')} />
        <Stat big={formatMinutes(s.minutes)} small={t('estimatedTime')} />
        <Stat big={String(s.total - s.done)} small={t('pending')} />
      </View>
      <View nativeID="print-columns-wide">
      {todays.map((tk) => {
        const r = today.results[tk.id];
        return (
          <View key={tk.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: colors.divider }}>
            <Box checked={r?.status === 'done'} />
            <Txt size={12} style={{ flex: 1 }}>
              {pick(tk.name, lang)}
              {r?.reason ? ` (${t('couldNotDo')}: ${reasonLabels[r.reason][lang]})` : ''}
            </Txt>
            <Txt size={11} color={colors.textMuted}>
              {r ? `${formatTime(r.at)} · ${r.byName}` : '—'}
            </Txt>
          </View>
        );
      })}
      </View>
      {restock.length > 0 && (
        <>
          <Txt weight="semibold" size={13} style={{ marginTop: 14, marginBottom: 4 }}>
            {t('productsToRestock')}
          </Txt>
          {restock.map((p) => (
            <Txt key={p.id} size={12}>
              • {pick(p.name, lang)}: {statusLabels[p.status][lang]}
            </Txt>
          ))}
        </>
      )}
    </Paper>
  );
}

function Stat({ big, small }: { big: string; small: string }) {
  return (
    <View style={{ alignItems: 'center' }}>
      <Txt weight="semibold" size={17}>
        {big}
      </Txt>
      <Txt size={11} color={colors.textMuted}>
        {small}
      </Txt>
    </View>
  );
}
